import { type AxiosResponse } from 'axios';

import type {
  SubscriptionPdl,
  Subscription,
  SubscriptionResource,
  PatchSubscriptionInstallationRequest,
  PatchSubscriptionPhysicalBatteryRequest,
  PhysicalBatteries,
  PhysicalBatteriesResource,
  PatchSubscriptionBatteryOptionsRequest,
  PatchSubscriptionProfileRequest,
  PatchSubscriptionInstallationAddressRequest,
  PatchSubscriptionCustomerDeclarationsRequest,
  PostBatteryChoicesRequest,
  BatteryChoices,
  BatteryChoicesResource,
  PatchConsumptionInformationsRequest,
  SubscriptionContractedPowers,
  SubscriptionEnergyOfferInformations,
  DirectDebit,
  SubscriptionIndividualContractor,
  SubscriptionCompanyContractor,
  PatchSubscriptionBillingInformationsRequest,
  ContractedPowersRessource,
  PatchSubscriptionConsentsRequest,
  PatchSubscriptionPromotionCodeRequest,
  SubscriptionSignature,
  SubscriptionSignatureResource,
} from '@/types/subscription.type';
import { ApiError } from '@/utils/error.util';

import axiosWrapper from '@mylight150/axios-wrapper';

const SUBSCRIPTION_API_BASE_URL = 'v3/subscription/fr';

export const getSubscription = async (idSubscription: string): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${idSubscription}`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.get(
    endpoint,
    {},
    {},
    false
  );
  return response.data;
};

export const postSubscriptionPdl = async (
  payload: Pick<SubscriptionPdl, 'operator' | 'value' | 'linkedActiveContractNumber'>
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/pdl`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.post(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionPdl = async (
  subscriptionId: string,
  payload: Pick<SubscriptionPdl, 'operator' | 'value' | 'linkedActiveContractNumber'>
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/pdl`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionInstallationAddress = async (
  subscriptionId: string,
  payload: PatchSubscriptionInstallationAddressRequest
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/installation-address`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionCustomerDeclarations = async (
  subscriptionId: string,
  payload: PatchSubscriptionCustomerDeclarationsRequest
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/customer-declarations`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionProfile = async (
  subscriptionId: string,
  payload: PatchSubscriptionProfileRequest
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/profile`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionBatteryOptions = async (
  subscriptionId: string,
  payload: PatchSubscriptionBatteryOptionsRequest
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/options/battery`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionEquipmentControlOptions = async (
  subscriptionId: string,
  hasAccepted: boolean
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/options/equipment-control`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    {
      requested: hasAccepted,
    },
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionFlexConsent = async (
  subscriptionId: string,
  hasConsent: boolean
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/consent/flex`;
  const response = await axiosWrapper.patch(endpoint, { consentToFlex: hasConsent }, {}, false);
  return response.data;
};

export const getPhysicalBatteries = async (): Promise<PhysicalBatteries> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/physical-batteries`;
  const response: AxiosResponse<PhysicalBatteriesResource> = await axiosWrapper.get(
    endpoint,
    {},
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionPhysicalBattery = async (
  subscriptionId: string,
  payload: PatchSubscriptionPhysicalBatteryRequest
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/physical-battery`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionInstallation = async (
  subscriptionId: string,
  payload: PatchSubscriptionInstallationRequest
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/installation`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const postSubscriptionBatteryChoices = async (
  subscriptionId: string,
  payload: PostBatteryChoicesRequest
): Promise<BatteryChoices> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/options/battery/choices`;
  const response: AxiosResponse<BatteryChoicesResource> = await axiosWrapper.post(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const getSubscriptionContractedPowersChoices =
  async (): Promise<SubscriptionContractedPowers> => {
    const endpoint = `${SUBSCRIPTION_API_BASE_URL}/energy-offer-informations/contracted-powers-choices`;
    const response: AxiosResponse<ContractedPowersRessource> = await axiosWrapper.get(
      endpoint,
      {},
      {},
      false
    );
    return {
      availableContractedPowers: response.data.map(item => ({
        kVA: item.kVA,
        price: {
          base: item.base,
          offPeakAndPeakHours: item.offPeakAndPeakHours,
        },
      })),
    };
  };

export const patchSubscriptionEnergyOfferInformations = async (
  subscriptionId: string,
  payload: SubscriptionEnergyOfferInformations
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/energy-offer-informations`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionDirectDebit = async (
  subscriptionId: string,
  payload: DirectDebit
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/direct-debit`;
  try {
    const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
      endpoint,
      payload,
      {},
      false
    );
    return response.data;
  } catch (error) {
    throw new ApiError(error);
  }
};

export const patchSubscriptionIndividualContractor = async (
  subscriptionId: string,
  payload: SubscriptionIndividualContractor
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/contractor/individual`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionCompanyContractor = async (
  subscriptionId: string,
  payload: SubscriptionCompanyContractor
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/contractor/company`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionBillingInformations = async (
  subscriptionId: string,
  payload: PatchSubscriptionBillingInformationsRequest
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/billing-informations`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionAnnualConsumption = async (
  subscriptionId: string,
  payload: PatchConsumptionInformationsRequest
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/annual-consumption`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionPromotionCode = async (
  subscriptionId: string,
  payload?: PatchSubscriptionPromotionCodeRequest
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/promotion-code`;
  try {
    const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
      endpoint,
      {
        value: payload?.value || null,
      },
      {},
      false
    );
    return response.data;
  } catch (error) {
    throw new ApiError(error);
  }
};

export const patchSubscriptionConsents = async (
  subscriptionId: string,
  payload: PatchSubscriptionConsentsRequest
): Promise<Subscription> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/consent/data-sharing`;
  const response: AxiosResponse<SubscriptionResource> = await axiosWrapper.patch(
    endpoint,
    payload,
    {},
    false
  );
  return response.data;
};

export const patchSubscriptionSignature = async (
  subscriptionId: string
): Promise<SubscriptionSignature> => {
  const endpoint = `${SUBSCRIPTION_API_BASE_URL}/${subscriptionId}/signature`;
  const response: AxiosResponse<SubscriptionSignatureResource> = await axiosWrapper.patch(
    endpoint,
    {},
    {},
    false
  );
  return response.data;
};
